/**
 * Sign-in, sign-out, and who is signed in.
 *
 * The browser does not talk to Supabase. It posts credentials to the backend,
 * which authenticates them against Supabase Auth and replies with an httpOnly
 * session cookie the browser cannot read. That is the whole reason this file is
 * short: there is no token to hold, refresh, persist or accidentally log.
 *
 * There is no mock provider any more. The previous build accepted the password
 * `demo` for any address when Supabase was unconfigured, which was a
 * development affordance that had to be defended against reaching production.
 * The backend owns sign-in now, and it has no such mode.
 */
import { apiGet, apiPost, type ApiFailure } from "./api-client";

/** Identity as the rest of the application sees it. Never carries a token. */
export interface UserProfile {
  email: string;
  name: string;
  organizationId: string;
  permissions: string[];
}

interface CurrentUserResponse {
  id: string;
  organization_id: string;
  email: string | null;
  permissions: string[];
}

export type SignInResult = { ok: true } | { ok: false; error: string };

/** `tom@example.com` -> `Tom`. Supabase may hold no display name. */
export function nameFromEmail(email: string): string {
  const local = email.split("@")[0] ?? email;
  const first = local.split(/[._-]/)[0] ?? local;
  return first.charAt(0).toUpperCase() + first.slice(1);
}

function toProfile(body: CurrentUserResponse): UserProfile {
  const email = body.email ?? "unknown@example.com";
  return {
    email,
    name: nameFromEmail(email),
    organizationId: body.organization_id,
    permissions: body.permissions,
  };
}

/**
 * The signed-in user, or null.
 *
 * Null is the ordinary unauthenticated answer, not an error: the route guard
 * reads it to decide whether to show the login screen. A backend that is
 * unreachable is also null — a workspace that cannot confirm a session must not
 * render one.
 */
export async function currentUser(): Promise<UserProfile | null> {
  const result = await apiGet<CurrentUserResponse>("/api/v1/auth/me");
  return result.ok ? toProfile(result.data) : null;
}

export async function signIn(email: string, password: string): Promise<SignInResult> {
  const result = await apiPost<CurrentUserResponse>("/api/v1/auth/login", {
    email: email.trim().toLowerCase(),
    password,
  });
  if (result.ok) return { ok: true };
  return { ok: false, error: describeSignInFailure(result.failure) };
}

/**
 * Sign-in failures are shown to whoever is at the keyboard, so they must not
 * distinguish "no such account" from "wrong password" — that is an account
 * enumeration oracle. The backend already returns one message for both; this
 * only makes the transport failures readable.
 */
function describeSignInFailure(failure: ApiFailure): string {
  switch (failure.kind) {
    case "unauthenticated":
      return failure.detail || "Those credentials were not accepted.";
    case "unreachable":
      return "Could not reach the server. Check your connection and try again.";
    case "error":
      return failure.status >= 500
        ? "The server is having trouble. Try again in a moment."
        : failure.detail || "Sign-in failed.";
  }
}

export async function signOut(): Promise<void> {
  // A failed sign-out still ends the session as far as this tab is concerned:
  // the caller navigates to /login either way, and the cookie is the backend's
  // to clear. Swallowing the result here avoids stranding someone on a page
  // they have asked to leave.
  await apiPost("/api/v1/auth/logout");
}
